// InfraShared.jsx — reusable UI atoms for Infrastructure & Facilities.

import React from 'react';

// ─── Table header / data cells ────────────────────────────────────────────────
export function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 whitespace-nowrap bg-white border-b border-gray-200">
      {children}
    </th>
  );
}

export function Td({ children, bold }) {
  return (
    <td className={`px-4 py-3.5 text-sm align-middle ${bold ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
      {children}
    </td>
  );
}

// ─── Three-dot action button ──────────────────────────────────────────────────
export function ActionDots({ onClick }) {
  return (
    <button onClick={onClick}
      className="text-green-600 hover:text-green-800 font-bold tracking-widest text-base px-1 leading-none">
      •••
    </button>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
export function EmptyState({ message = 'No records found.' }) {
  return <div className="py-14 text-center text-sm text-gray-400">{message}</div>;
}

// ─── Badges ───────────────────────────────────────────────────────────────────
const FUNC_COLORS = {
  active: { dot: 'bg-green-500', text: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', label: 'Active' },
  under_maintenance: { dot: 'bg-amber-400', text: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'Under Maintenance' },
  inactive: { dot: 'bg-red-500', text: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Inactive' },
  under_rehabilitation: { dot: 'bg-blue-500', text: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'Under Rehabilitation' },
  decommissioned: { dot: 'bg-gray-400', text: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb', label: 'Decommissioned' },
};

// Legacy vocabulary (pre v2.0 field-dictionary fix) — kept so facilities
// saved under the old ENUM still resolve to a badge instead of falling
// through to the "Unknown" fallback.
const FUNC_ALIASES = {
  fully_functional: 'active',
  'fully functional': 'active',
  functional: 'active',
  partially_functional: 'under_maintenance',
  'partially functional': 'under_maintenance',
  non_functional: 'inactive',
  'non functional': 'inactive',
  'non-functional': 'inactive',
  under_construction: 'under_rehabilitation',
  'under construction': 'under_rehabilitation',
};

export function normalizeFunctionalStatus(status) {
  const text = String(status ?? '').trim();
  if (!text) return 'inactive';
  const lower = text.toLowerCase().replace(/[\s-]+/g, '_');
  if (FUNC_COLORS[lower]) return lower;
  if (FUNC_ALIASES[lower]) return FUNC_ALIASES[lower];
  if (FUNC_COLORS[text]) return text;
  return lower;
}

export function getFunctionalStatusMeta(status) {
  const key = normalizeFunctionalStatus(status);
  return FUNC_COLORS[key] ?? {
    dot: 'bg-gray-400',
    text: '#6b7280',
    bg: '#f3f4f6',
    border: '#e5e7eb',
    label: String(status ?? '').trim() || 'Unknown',
  };
}

export function FunctionalBadge({ status }) {
  const s = getFunctionalStatusMeta(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
      style={{ color: s.text, backgroundColor: s.bg, borderColor: s.border }}
    >
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

const LIC_COLORS = { Licensed: 'text-green-700', Unlicensed: 'text-red-600', Pending: 'text-amber-600', Exempt: 'text-gray-500' };
export function LicensedBadge({ status }) {
  return <span className={`text-xs font-semibold ${LIC_COLORS[status] ?? 'text-gray-500'}`}>{status}</span>;
}

const PRIORITY_COLORS = { High: 'text-red-600', Medium: 'text-amber-600', Low: 'text-green-600', None: 'text-gray-400' };
export function PriorityBadge({ priority }) {
  return <span className={`text-xs font-semibold ${PRIORITY_COLORS[priority] ?? 'text-gray-500'}`}>{priority}</span>;
}

// Compliance score chip (Inspection Registry) — colour by threshold
export function ComplianceScore({ score }) {
  const n = Number(score);
  const tone = n >= 80 ? 'text-green-700' : n >= 60 ? 'text-amber-600' : 'text-red-600';
  return <span className={`text-xs font-bold ${tone}`}>{score}</span>;
}

// ─── Select dropdown ──────────────────────────────────────────────────────────
const selectClass =
  'w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm text-gray-700 ' +
  'focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none cursor-pointer';

export function SelectField({ label, name, value, onChange, options, placeholder = 'All' }) {
  return (
    <div className="flex flex-col gap-1.5 flex-1 min-w-[130px]">
      {label && <label className="text-xs font-semibold text-gray-600">{label}</label>}
      <div className="relative">
        <select name={name} value={value ?? ''} onChange={onChange} className={selectClass}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
          fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

// ─── Search + action bar ──────────────────────────────────────────────────────
// onCreateNew is OPTIONAL — omit it for read-only tabs (e.g. Validation History).
export function SearchBar({ value, onChange, onSearch, onCreateNew }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row">
      <div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 focus-within:border-transparent focus-within:ring-2 focus-within:ring-green-500">
        <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <circle cx={11} cy={11} r={8} /><path d="M21 21l-4.35-4.35" />
        </svg>
        <input value={value} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch?.()}
          placeholder="Search here"
          className="flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder-gray-400" />
      </div>
      <button onClick={onSearch}
        className="rounded-lg border border-green-200 bg-[#D9FBD4] px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-green-600 transition-colors hover:bg-green-100 sm:w-auto">
        Search
      </button>
      {onCreateNew && (
        <button onClick={onCreateNew}
          className="flex items-center gap-1.5 rounded-lg bg-green-700 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-white transition-colors hover:bg-green-800 sm:w-auto">
          <span className="font-bold text-base leading-none">+</span> Create New
        </button>
      )}
    </div>
  );
}

// ─── Apply button / Period input / Filter row wrapper ─────────────────────────
export function ApplyButton({ onClick }) {
  return (
    <div className="self-end">
      <button onClick={onClick}
        className="px-6 py-2.5 bg-[#D9FBD4] border border-green-200 text-green-600 text-sm font-semibold rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap">
        Apply
      </button>
    </div>
  );
}

export function FilterRow({ children }) {
  return <div className="flex gap-3 flex-wrap items-end mb-6">{children}</div>;
}

export function PeriodInput({ value, onChange }) {
  const cls = 'w-full bg-white border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent';
  return (
    <div className="flex flex-col gap-1.5 flex-1 min-w-[130px]">
      <label className="text-xs font-semibold text-gray-600">Set Period</label>
      <div className="relative">
        <input type="text" placeholder="DD/MM/YY" value={value ?? ''} onChange={onChange} className={cls} />
        <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
          fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

// ─── Map marker colour by functional status ────────────────────────────────────
export const MARKER_COLORS = {
  active: '#22c55e',
  under_maintenance: '#f59e0b',
  inactive: '#ef4444',
  under_rehabilitation: '#3b82f6',
  decommissioned: '#9ca3af',
};
